import { ASSET_STATUS, ASSET_TYPE, assetIsPublished, assetIsReady, bookmarkAsset, confirm, DTEAcknowledgement, formatFileResponse, getAsset, getAssetVersions, getMetadatas, handleRequestError, IAsset, IAssetResponse, IAssetVersion, IFile, InfoBox, isUser, likeAsset, listAssetRepository, Loading, Logger, LoginRequired, Page, Select, SOURCE_TYPE, submitAsset, toast, ToggleButton, updateAsset, useFeatureIsEnabled, UserLink, useTermsAndConditionsLink, useUser, useUserEmail, useUserHasRole } from '@desp-aas/desp-ui-fwk';
import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmailRequiredInfoBox } from '../../components/EmailRequiredInfoBox/EmailRequiredInfoBox';
import Navigation from '../../components/Navigation/Navigation';
import { IAssetFilter } from '../../utils/catalog';
import { icons } from '../../utils/icons';
import { AssetImage } from './AssetImage/AssetImage';
import { AssetMetadata } from './AssetMetadata/AssetMetadata';
import './AssetPage.css';
import { removeAssetCreationAnalytics, trackAssetCreationAnalytics, useAssetPageAnalytics } from './AssetPageAnalytics';
import { AssetStatus } from './AssetStatus';
import { checkFieldHasUpdates, checkFileIsNew, checkMetadataFieldHasUpdates } from './common';
import { Details } from './Details/Details';
import { Discussions } from './Discussions/Discussions';
import Files from './Files/Files';
import { Options } from './Options/Options';
import { Overview } from './Overview/Overview';
import { RejectionReason } from './RejectionReason/RejectionReason';

const logger = new Logger('Page', 'AssetPage');

type IAssetTab = {
    name: string
    // content: (props: IAssetTabProps) => ReactNode
    label: ReactNode
}

const TABS: { [key: string]: IAssetTab } = {
    'overview': {
        label: <>Overview</>,
        name: 'overview',
    },
    'details': {
        label: <>Details</>,
        name: 'details',
    },
    'files': {
        label: <>Files</>,
        name: 'files',
    },
    'discussions': {
        label: <>Discussions</>,
        name: 'discussions',
    },
    'options': {
        label: <>Options</>,
        name: 'options',
    },
}


const removeEmpty = (obj: any) => {
    for (const entry of Object.entries(obj)) {
        if (entry[1] === '') {
            delete obj[entry[0]];
        }
    }
}

export type IPendingActions = {
    pending: boolean
    required?: boolean
    title: ReactNode
    description: ReactNode

}[]

const USABILITY_SCORE_MAX = 10;

let fetchLoop: NodeJS.Timeout|undefined;
let fetchFilesLoop: NodeJS.Timeout|undefined;

const clearLoop = () =>{
    if(fetchLoop){
        clearInterval(fetchLoop);
        fetchLoop = undefined;
    }
}


export const AssetPage = () => {
    const pageRef = useRef<HTMLInputElement>(null);
    const [ assetResponse, setAssetResponse ] = useState<IAssetResponse>();
    const [asset, setAsset] = useState<IAsset>();
    const { asset_id } = useParams() as { asset_id: string };

    const [loadingAsset, setLoadingAsset] = useState(true);
    const [editMode, setEditMode] = useState(false);

    const [loadingAssetMetadata, setLoadingAssetMetadata] = useState(true);
    const [allMetadata, setAllMetadata] = useState<IAssetFilter[]|undefined>();
    const [assetMetadata, setAssetMetadata] = useState<IAssetFilter[]|undefined>();
    
    const [loadingFiles, setLoadingFiles] = useState(true);
    const [files, setFiles] = useState<IFile[]>();

    // const [assetReady, setAssetReady] = useState(false);
    const [assetPublished, setAssetPublished] = useState(false);

    const [tabs, setTabs] = useState<IAssetTab[] | undefined>();
    const [selectedTab, setSelectedTab] = useState<IAssetTab | undefined>();
    const [isOwner, setIsOwner] = useState(false);
    const [assetLabel, setAssetLabel] = useState<ReactNode>();

    const [ assetVersions, setAssetVersions ] = useState<IAssetVersion[] | undefined>();
    const [ latestVersionDate, setLatestVersionDate ] = useState<string | undefined>();
    const [ loadingAssetVersions, setLoadingAssetVersions ] = useState(true);
    const [ selectedVersion, setSelectedVersion ] = useState<IAssetVersion | undefined>();

    const [pendingActions, setPendingActions] = useState<IPendingActions | undefined>();
    const [requiredActions, setRequiredActions] = useState< number | undefined>();
    const [requiredCount, setRequiredCount] = useState< number | undefined>();
    const [usabilityScore, setUsabilityScore] = useState<string | number | undefined>();
    const [ imageCacheID, setImageCacheID ] = useState(Math.random())


    const [ submiting, setSubmiting ] = useState(false);
    const [ updating, setUpdating ] = useState(false);

    const [ initialised, setInitialised ] = useState(false);
    const [ liking, setLiking ] = useState(false);
    const [ bookmarking, setBookmarking ] = useState(false);

    const [ updatingAsset, setUpdatingAsset ] = useState(false);
    const [ imageHasBeenEdited, setImageHasBeenEdited ] = useState(false);

    const user = useUser();
    const currentEmail = useUserEmail();

    const isEmailEnabled = useFeatureIsEnabled("email_enabled");
    const isGitlabEnabled = useFeatureIsEnabled("gitlab_enabled");

    const navigate = useNavigate();

    useAssetPageAnalytics({ asset });

    const userCanDeleteAsset = useUserHasRole("moderation");


    useEffect(() => {
        setIsOwner(!!(assetResponse && isUser(user, assetResponse?.draft?.despUserId)))
    }, [user, assetResponse])

    useEffect(() => {
        setAssetLabel(asset ? asset.type : undefined)
    }, [asset])

    useEffect(() => {
        setLoadingAssetMetadata(true);
        getMetadatas()
            .then((metadata) => {
                setAllMetadata(metadata)
                setLoadingAssetMetadata(false)
            })
            .catch((e: any) => {
                logger.error('Error during fetchAssetMetadata', e);
                setLoadingAssetMetadata(false)
            })
    }, [])

    useEffect(() => {
        if(!asset?.type){
            setAssetMetadata(undefined);
            return;
        }
        setAssetMetadata(allMetadata?.filter((metadata) => metadata.asset_type?.includes(asset.type) || metadata.asset_type === undefined || metadata.asset_type === null))
    }, [asset?.type, allMetadata])

    

    useEffect(() => {
        if(asset && isOwner){

            let pendingActions: IPendingActions = [
                {
                    pending: !(asset.metadata?.description && !!asset.metadata.description), required: true,
                    title: <>Add Description </>,
                    description: <>Share specifics about the context, and inspiration behind your {assetLabel}</>
                },
                {
                    pending: asset.categoryId === undefined, required: true,
                    title: <>Specify {assetLabel} category </>,
                    description: <>Specify the category of your {assetLabel}</>
                },
                // {
                //     pending: !asset.isVisible, required: true,
                //     title: <>Set to visible</>,
                //     description: <>Set your {assetLabel} to public.</>
                // },
                
                // { 
                //     pending: asset.,
                //     title: <>Add Description </>,
                //     description: <>Share specifics about the context, and inspiration behind your dataset</>
                // }
            ];
    
            if(asset.source !== "external"){
                pendingActions?.push({
                    pending: !(files && files.length > 0), required: true,
                    title: <>Add files </>,
                    description: <>Upload files for your {assetLabel}</>
                })
            }

            // Non required
            // pendingActions = pendingActions.concat([
            //     {
            //         pending: asset.metadata?.type === undefined,
            //         title: <>Specify {assetLabel} type</>,
            //         description: <>Specify the type of your {assetLabel}</>
            //     },
            // ])
    
            setPendingActions(pendingActions);
    
            setUsabilityScore(pendingActions ? pendingActions?.filter((pa) => pa.pending === false).length : 0);
            setRequiredActions(pendingActions ? pendingActions?.filter((pa) => pa.pending === false && pa.required === true).length : 0);
            setRequiredCount(pendingActions ? pendingActions?.filter((pa) => pa.required === true).length : 0);

            // let usabilityScore: number | string = pendingActions ? pendingActions?.filter((pa) => pa.pending === false).length : 0;
            // usabilityScore = (pendingActions ? (usabilityScore / pendingActions.length) * USABILITY_SCORE_MAX : 0)
            // if(!Number.isInteger(usabilityScore)){
            //     usabilityScore = usabilityScore.toFixed(2)
            // }
            // setUsabilityScore(usabilityScore);
        }


    }, [assetLabel, asset, isOwner, files])

    
    useEffect(()=>{
        if(asset && asset.status && [ASSET_STATUS.validation, ASSET_STATUS.modified].includes(asset.status)){
            if(!fetchLoop)
                fetchLoop = setInterval(()=>{ fetchAsset(); }, 5000)
        }
        else{
            if(fetchLoop){
                clearLoop()
            }
        }

        return ()=>{
            clearLoop();
        }
    }, [ asset?.status ])


    useEffect(() => {
        setAssetPublished(assetIsPublished(assetResponse?.public || assetResponse?.draft));
    }, [assetResponse, editMode])

    useEffect(()=>{
        if(assetResponse){
            let _asset: IAsset|undefined =  (editMode || !assetPublished) ? assetResponse?.draft : assetResponse?.public;
            if(editMode && _asset){
                _asset = {
                    ..._asset,
                    isLiked: !!assetResponse?.draft?.isLiked,
                    isBookmarked: !!assetResponse?.draft?.isBookmarked
                }
            }
            if(_asset)
                setAsset(_asset)
        }
    }, [ assetResponse, assetPublished, editMode ])


    useEffect(() => {
        logger.debug("[CheckUser] checking  is user", asset?.despUserId, asset?.metadata?.description)
        if (
            asset && asset.despUserId
            && isOwner
            // && !assetIsPublished(asset)
        ) {
            logger.debug("[CheckUser] is user")
            if (!editMode && !assetIsPublished(asset)) setEditMode(true)
        }
        else {
            logger.debug("[CheckUser] is not user")
            if (editMode) setEditMode(false);
        }
    }, [asset, user, isOwner])

    // useEffect(() => {
    //     setAssetReady(assetIsReady(asset, files));
    // }, [asset, editMode, files])

    const assetReady: boolean = useMemo(() => {
        return assetIsReady(asset, files) && ( isEmailEnabled ? !!currentEmail : true );
    }, [asset, files, editMode, currentEmail, isEmailEnabled, imageHasBeenEdited ])

    useEffect(() => {
        // Handle tabs
        if (asset) {
            const filesTab = isGitlabEnabled ? [ TABS.files ] : [];
            const discussionsTab = assetPublished ? [ TABS.discussions ] : [];
            
            let tabs: IAssetTab[] = [TABS.overview, TABS.details, ...filesTab, ...discussionsTab];
            // Why ?
            if (asset.source !== SOURCE_TYPE.user)
                tabs = [TABS.overview, TABS.details, ...discussionsTab]
            else if (ASSET_TYPE.application)
                // tabs = [TABS.overview, TABS.details, TABS.files, TABS.incidents, ...!assetPublished ? [TABS.discussions] : []]
                tabs = [TABS.overview, TABS.details, ...filesTab, ...discussionsTab]
            else
                tabs = [TABS.overview, TABS.details, ...filesTab, ...discussionsTab];

            if(isOwner || userCanDeleteAsset){
                tabs.push(TABS.options)
            }

            setTabs(tabs);

            if (selectedTab) {
                if (!tabs.includes(selectedTab))
                    setSelectedTab(tabs[0])
            }
            else {
                setSelectedTab(tabs[0])
            }
        }
        else {
            setTabs(undefined)
            setSelectedTab(undefined)
        }
    }, [asset, editMode, assetPublished, isOwner, userCanDeleteAsset])

    const fetchAsset = async (version?: IAssetVersion) => {
        setLoadingAsset(true)
        try {
            let _version = version || selectedVersion;
            // const _asset = asset_id === "test" ? TEST_ASSET : await getAsset(asset_id);
            const assetResponse = await getAsset(asset_id, editMode ? undefined : (_version ? _version.latest ? undefined : _version.marker : undefined));

            setAssetResponse(assetResponse);
            setImageCacheID(Math.random())
        }
        catch (e: any) {
            logger.error('Error during fetchAsset', e);
            if(e?.response && e.response.status === 404){
                toast(<>The asset is either not ready or does not exist</>, { type: "warning" })
            }
            else{
                handleRequestError(e, { defaultMessage: <>An error has occured during asset retrieval, please try again later</> })
            }

            navigate(`/catalog/dataset`)
        }
        finally {
            setLoadingAsset(false)
            setInitialised(true)
        }
    }


    
    const fetchFiles = async (withoutLoading?: boolean) => {
        if(!asset || asset.source === 'external') return;
        if (!withoutLoading) setLoadingFiles(true)
        if(!asset.repository){
            return toast(<>Sorry the {assetLabel} does not have a git initialised, please contact support to resolve your issue.</>, { type: 'error' })
        }
        try {
            const files = (await listAssetRepository(asset.repository.id, "/"));
            setFiles(formatFileResponse(files.files, files.folders));
        }
        catch (e: any) {
            logger.error('Error during fetchFiles', e);
            handleRequestError(e, { defaultMessage: <>Could not retrieve files, please try again later</> });
        }
        finally {
            if (!withoutLoading) setLoadingFiles(false)
        }
    }
    const fetchAssetVersions = (updateSelectedVersion?: boolean) => {
        setLoadingAssetVersions(true);
        getAssetVersions(asset_id)
            .then((_versions) => {
                const versions = _versions.sort((a, b) => b.version - a.version);
                if(versions[0]){
                    versions[0].latest = true;
                }
                setLatestVersionDate(versions[0]?.created_at);
                setAssetVersions(versions);
                if(!selectedVersion || updateSelectedVersion){
                    setSelectedVersion(versions[0])
                }
            })
            .finally(() => {
                setLoadingAssetVersions(false);
            })
            
    }

    const filesHaveUpdates = useMemo(() => {
        if (!editMode || !files || !latestVersionDate) return false;
        for(const file of files){
            if(checkFileIsNew(file, latestVersionDate)) return true;
        }
        return false;

    }, [ files, assetVersions, latestVersionDate ])

    useEffect(() => {
        fetchAsset();
        fetchAssetVersions();
    }, [asset_id])

    
    useEffect(() => {
        fetchAsset();
        if(editMode){
            fetchFilesLoop = setInterval(() => {
                fetchFiles(true);
            }, 10000);
        }
        else{
            clearInterval(fetchFilesLoop);
        }
        return () => {
            clearInterval(fetchFilesLoop);
        }
    }, [editMode])

    useEffect(() => {
        if(selectedVersion && initialised){
            fetchAsset()
        }
    }, [selectedVersion])

    useEffect(() => {
        fetchFiles()
    }, [ asset ])

    const onAssetUpdate = async (updates: Partial<IAsset>) => {
        
        logger.info("[onAssetUpdate] updating asset")
        if (!asset) {
            logger.error("[onAssetUpdate] no asset")
            return;
        }
        if(updating){
            logger.error("[onAssetUpdate] already updating")
            return;
        }
        setUpdating(true)
        try {
            let updatedAsset: IAsset = { ...asset, ...updates };
            removeEmpty(updatedAsset)
            removeEmpty(updatedAsset.metadata)
            let newAsset = await updateAsset(updatedAsset);
            let mergedAsset = {
                ...asset, ...newAsset
            }
            setAsset(mergedAsset)
            setAssetResponse((prev)=>({
                ...prev,
                draft: mergedAsset
            }))
            // logger.info("fetching asset")
            // fetchAsset(true);
        }
        catch (e) {
            logger.error("[onAssetUpdate] error during update", e);
            handleRequestError(e)
        }
        finally {
            setUpdating(false)
        }
    }

    const onLikeClick = useCallback(() => {
        if (asset) {
            setLiking(true)
            likeAsset(asset)
                .then(() => {
                    fetchAsset()
                    .then(()=>{
                        setLiking(false)
                    })
                })
                .catch((e: any) => {
                    logger.error('Error during asset like', e)
                    handleRequestError(e)
                    setLiking(false)
                })
        }
    }, [asset])

    const onBookmarkClick = useCallback(() => {
        if (asset) {
            setBookmarking(true)
            bookmarkAsset(asset)
                .then(() => {
                    fetchAsset()
                    .then(()=>{
                        setBookmarking(false)
                    })
                })
                .catch((e: any) => {
                    logger.error('Error during asset bookmark', e)
                    handleRequestError(e)
                    setBookmarking(false)
                })
        }
    }, [asset])

    const onSubmit = useCallback(()=>{
        if(assetReady && asset){
            let step = assetPublished ? "revalidate" : "submit"
            confirm({
                title: `${step.charAt(0).toUpperCase() + step.slice(1)}`,
                message: <>
                    By confirming you agree to the <a href={termsAndConditionsLink} target='_blank'>Terms & conditions</a>.
                    { 
                        asset.source === SOURCE_TYPE.user &&
                        <>
                            <br/>
                            <br/>
                            { DTEAcknowledgement }
                        </>
                    }
                </>,
                onConfirm: ()=>{
                    setSubmiting(true)
                    submitAsset({...asset })
                    .then(() => {
                        if(asset.status === ASSET_STATUS.creation){
                            trackAssetCreationAnalytics(asset);
                        }
                        toast(<>Your {assetLabel} has successfully been { asset.status === ASSET_STATUS.creation ? 'submitted' : 'submitted for modification'  }</>, { type: "success" });
                        fetchAsset()
                        .then(()=>{
                            if(asset.status === ASSET_STATUS.creation){
                                removeAssetCreationAnalytics(asset);
                            }
                        });
                        
                    })
                    .catch((e: any) => {
                        logger.error('Error during asset onSubmit', e)
                        handleRequestError(e, { defaultMessage: `An error has occured during ${assetLabel} retrieval, please try again later` })
                    })
                    .finally(()=>{
                        setSubmiting(false)
                    })
                }
            })
        }

    }, [ assetReady, asset])


    const getStatus = useCallback(() =>{
        
        switch(asset?.status){
            case ASSET_STATUS.rejected:
                return ( <>The {assetLabel} is <span className="rejected">{icons.asset_status.rejected} rejected</span></>)
            case ASSET_STATUS.creation:
                return ( <>The {assetLabel} is <span className="creating">{icons.asset_status.creation} creating</span></>)
            case ASSET_STATUS.validation:
                return ( <>The {assetLabel} is <span className="pending">{icons.asset_status.validation} awaiting approval</span></>)
            case ASSET_STATUS.published:
                return ( <>The {assetLabel} is <span className="published">{icons.asset_status.published} published</span></>)
            case ASSET_STATUS.modified:
                return ( <>The {assetLabel} is <span className="modified">{icons.asset_status.modified} awaiting modification approval</span></>)
            default:
                return ( <>The {assetLabel} is <span>{asset?.status}</span></>)
        }
    }, [ asset, assetLabel ]);



    useEffect(() => {
        // console.log("asset status", asset?.status)

        if(asset?.status && asset?.status !== ASSET_STATUS.published){
            setUpdatingAsset(true)
        }
        else{
            if(updatingAsset){
                fetchAssetVersions(true)
            }
            setUpdatingAsset(false)
        }
    }, [ asset?.status, updatingAsset ])

    
    const assetHasUpdates = useMemo(() => {
        console.log('asset has updates?', assetResponse?.draft, assetResponse?.public);
        if(imageHasBeenEdited) return true;
        if(filesHaveUpdates) return true;
        if(asset && isOwner && assetResponse?.draft && assetResponse?.public){
            for(const field of ['categoryId', 'name']){
                if(checkFieldHasUpdates(assetResponse.public, assetResponse.draft, field as (keyof IAsset))){
                    console.log(`asset has updates? updated field: ${field}  ${assetResponse.public[field as keyof IAsset]} => ${assetResponse.draft[field as keyof IAsset]}`);
                    return true;
                }
            }
            for(const field in assetResponse.draft.metadata){
                if(['last_commit_id'].includes(field)){
                    continue;
                }
                if(checkMetadataFieldHasUpdates(assetResponse.public, assetResponse.draft, field)){
                    console.log(`asset has updates? updated metadata field: ${field}  ${assetResponse.public.metadata?.[field]} => ${assetResponse.draft.metadata?.[field]}`);

                    return true;
                }
            }
            return false;
        }
        else{
            return true;
        }
    }, [isOwner, assetResponse, filesHaveUpdates, imageHasBeenEdited ])

    const termsAndConditionsLink = useTermsAndConditionsLink();

    return (
        <Page
            id="asset-page"
            // fixedHeight
            pageRef={pageRef}
            fixedPageThreshold={1400}
            // footer={{ fixed: true }}
        // background2
        >
            <Navigation />
            <div id="asset-page-content">
            {/* <div className="fixed-page-content"> */}
                {
                    initialised && asset
                    // && false
                        ?
                        <>
                            <div id="asset-banner" className="section">
                                <div id="asset-banner-content">
                                    <div id="asset-banner-top">
                                        {
                                            !assetPublished &&
                                            <div id="asset-banner-finalise">
                                                Finalise {assetLabel}
                                            </div>
                                        }
                                        {
                                            assetPublished && isOwner &&
                                            <div id="asset-edit-mode">
                                                <ToggleButton value={editMode} onToggle={(editMode) => setEditMode(editMode)} />
                                                <label>Edit mode</label>
                                            </div>
                                        }
                                        <AssetStatus asset={asset} editMode={editMode} isOwner={isOwner} />
                                        {
                                            asset &&
                                            assetPublished &&
                                            <div id="asset-banner-top-right">
                                                {
                                                    loadingAsset &&
                                                    <Loading/>
                                                }
                                                {
                                                    !editMode &&
                                                    asset.source !== SOURCE_TYPE.external &&
                                                    assetVersions &&
                                                    assetVersions.length > 0 &&
                                                    <Select
                                                        id="asset-version-select"
                                                        options={assetVersions?.map((version, idx) => ({ label: `v${version.version}${idx === 0 ? ' (latest)' : ''}`, value: version.version })) || []}
                                                        onChange={(version) => {
                                                            let v = assetVersions?.find((v) => v.version === version)
                                                            setSelectedVersion(v)
                                                            // fetchAsset(v)
                                                        }}
                                                        value={selectedVersion?.version}
                                                    />
                                                }
                                                <div id="asset-banner-buttons">
                                                    <LoginRequired
                                                        small
                                                        message="You must be signed in to like and bookmark"
                                                    >
                                                        <div
                                                            id="asset-banner-like" 
                                                            onClick={onLikeClick}
                                                            className={classNames(
                                                                "asset-banner-operation", 
                                                                { "toggled": asset.isLiked, "disabled": liking }
                                                            )}
                                                        >
                                                            {
                                                                liking ? <Loading grey /> :
                                                                asset.isLiked
                                                                    ? icons.assets.isLiked
                                                                    : icons.assets.isNotLiked
                                                            }
                                                            <div className="asset-banner-operation-label">
                                                                {
                                                                    asset.isLiked
                                                                        ? (liking ? <>Unliking...</> : <>Unlike</>)
                                                                        : (liking ? <>Liking...</> : <>Like</>)
                                                                }
                                                            </div>
                                                            <div className="asset-banner-operation-count">
                                                                {asset.likes_count || 0}
                                                            </div>
                                                        </div>
                                                        <div
                                                            id="asset-banner-bookmark"
                                                            className={classNames(
                                                                "asset-banner-operation", 
                                                                { "toggled": asset.isBookmarked, "disabled": bookmarking }
                                                            )}
                                                            onClick={onBookmarkClick}
                                                        >
                                                            {
                                                                bookmarking ? <Loading grey /> :
                                                                asset.isBookmarked
                                                                    ? icons.assets.isBookmarked
                                                                    : icons.assets.isNotBookmarked
                                                            }
                                                            <div className="asset-banner-operation-label">
                                                                {
                                                                    asset.isBookmarked
                                                                        ? (bookmarking ? <>Unbookmarking...</> : <>Unbookmark</>)
                                                                        : (bookmarking ? <>Bookmarking...</> : <>Bookmark</>)
                                                                }
                                                            </div>
                                                            <div className="asset-banner-operation-count">
                                                                {asset.bookmarks_count || 0}
                                                            </div>
                                                        </div>
                                                        {/* <DivWithTooltip
                                                            tooltip={`${asset.isBookmarked ? 'unbookmark' : 'bookmark'} ${assetLabel}`}
                                                            id="asset-banner-bookmark" 
                                                            className={classNames({ "bookmarked": asset.isBookmarked, "disabled": bookmarking })}
                                                            onClick={onBookmarkClick}
                                                        >
                                                            {
                                                                asset.isBookmarked
                                                                    ? icons.assets.isBookmarked
                                                                    : icons.assets.isNotBookmarked
                                                            }
                                                        </DivWithTooltip> */}
                                                        {/* <div 
                                                            id="asset-banner-bookmark" 
                                                            className={classNames({ "bookmarked": asset.isBookmarked, "disabled": bookmarking })}
                                                            onClick={onBookmarkClick}
                                                        >
                                                            
                                                            {
                                                                asset.isBookmarked
                                                                    ? icons.assets.isBookmarked
                                                                    : icons.assets.isNotBookmarked
                                                            }
                                                        </div> */}
                                                    </LoginRequired>

                                                </div>
                                            </div>
                                        }
                                    </div>
                                    <div id="asset-banner-metadata">
                                        <div id="asset-banner-type">
                                            {assetLabel}
                                        </div>
                                        <div id="asset-banner-header">
                                            {
                                                asset.despUserId &&
                                                <>
                                                    {/* <NavLink id="asset-banner-header-user" to={getUserLink(asset.despUserId)}> */}
                                                        
                                                    <UserLink
                                                        username={asset.despUserId}
                                                    // username={props.post.display_username}
                                                    />
                                                        {/* {asset.despUserId} */}
                                                    {/* </NavLink> */}
                                                    <div id="asset-banner-header-separator">
                                                        /
                                                    </div>
                                                </>
                                            }
                                            <div id="asset-banner-header-title">
                                                {asset.name}
                                            </div>
                                        </div>



                                        {
                                            // isOwner &&
                                            editMode &&
                                            pendingActions && 
                                            <div id="asset-validation">
                                                <div id="asset-pending-actions">
                                                    <div id="asset-pending-action-list" className="simple-scrollbar">
                                                        {
                                                            pendingActions?.map((action, idx) => (
                                                                action.pending
                                                                    ? <div className={classNames({ "pending-action": true, "action-required": action.required })} key={idx}>
                                                                        <div className="pending-action-title">
                                                                            {action.title}
                                                                            {
                                                                                action.required &&
                                                                                <div className="pending-action-required">*</div>
                                                                            }
                                                                        </div>
                                                                        <div className="pending-action-description">
                                                                            {action.description}
                                                                        </div>
                                                                    </div>
                                                                    : null
                                                            ))
                                                        }
                                                    </div>
                                                    <div id="asset-usability-score">
                                                        <div id="asset-usability-score-title">
                                                            Pending Actions
                                                        </div>
                                                        {/* <div id="asset-usability-score-content">
                                                            <div id="asset-usability-score-label">
                                                                Usability score:
                                                            </div>
                                                            <div id="asset-usability-score-value">
                                                                <span>{usabilityScore}</span> / {pendingActions.length || 0}
                                                            </div>
                                                        </div> */}
                                                        {
                                                            // !assetPublished &&
                                                            // <div id="asset-usability-score-content">
                                                            //     <div id="asset-usability-score-label">
                                                            //         Publish requirements:
                                                            //     </div>
                                                            //     <div id="asset-usability-score-value">
                                                            //         <span>{requiredActions}</span> / {requiredCount}
                                                            //     </div>
                                                            // </div>
                                                        }

                                                        <div id="asset-status">
                                                            { getStatus() }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        }


                                        
                                    </div>
                                </div>
                                
                                <AssetImage 
                                    asset={asset} editMode={editMode} pageRef={pageRef} 
                                    imageHasBeenEdited={imageHasBeenEdited}
                                    setImageHasBeenEdited={setImageHasBeenEdited}
                                    imageCacheID={imageCacheID}
                                    setImageCacheID={setImageCacheID}
                                />

                            </div>

                            <RejectionReason editMode={editMode} assetResponse={assetResponse}/>

                            {
                                editMode &&
                                <div className="section">
                                    <InfoBox
                                        // title="Information"
                                        type="info"
                                        className="asset-page-info-box"
                                    >
                                        The asset must be in english.
                                    </InfoBox>
                                </div>
                            }

                            <div className="section" id="asset-middle-section">
                                
                                <div id="asset-tabs" className='simple-scrollbar'>
                                    {
                                        tabs?.map((tab, idx) => (
                                            <div
                                                className={classNames({ "asset-tab": true, "selected": selectedTab && selectedTab === tab })}
                                                key={tab.name}
                                                onClick={() => setSelectedTab(tab)}
                                                id={`asset-tab-${tab.name}`}
                                            >
                                                <div className="asset-tab-checkbox" />
                                                <label>{tab.label}</label>
                                            </div>
                                        ))
                                    }
                                </div>

                                <div id="asset-middle-section-right">
                                    {
                                        editMode &&
                                        <EmailRequiredInfoBox/>
                                    }
                                    {
                                        (
                                            // true ||
                                            updating 
                                        ) && 
                                        <div id="updating-asset">
                                            <span>
                                                <Loading/>
                                            </span>
                                            Updating {assetLabel}...
                                        </div>
                                    }
                                    {
                                        // !assetPublished &&
                                        editMode &&
                                        <div id="submit-section">
                                            <div 
                                                id="submit" 
                                                className={classNames({ 
                                                    "button medium themed": true, 
                                                    "disabled": !assetReady || submiting || !assetHasUpdates
                                                })}
                                                onClick={onSubmit}
                                            >
                                                {
                                                    assetPublished
                                                    ?
                                                        submiting
                                                        ? <><Loading/> Revalidating</>
                                                        : <>Revalidate</>
                                                    :
                                                        submiting
                                                        ? <><Loading/> Submiting</>
                                                        : <>Submit</>
                                                }
                                            </div>
                                            {/* <div id="submit-text">
                                            </div> */}
                                        </div>
                                    }
                                </div>

                            </div>


                            <div id="asset-container" className={classNames({ [selectedTab?.name || '']: true })}>
                                <div id="asset-container-content" className="section">

                                    <div id="asset-content" className={classNames({ [selectedTab?.name || '']: true,  "simple-scrollbar": true })}>
                                        {
                                            asset && selectedTab &&
                                            (
                                                selectedTab.name === 'overview'
                                                    ? <Overview 
                                                        asset={asset} editMode={editMode} updateAsset={onAssetUpdate} updating={updating} loadingAssetMetadata={loadingAssetMetadata} 
                                                        assetMetadata={assetMetadata} selectedVersion={selectedVersion}assetVersions={assetVersions}
                                                        assetResponse={assetResponse}
                                                    />
                                                    : selectedTab.name === 'details'
                                                        ? <Details 
                                                            asset={asset} editMode={editMode} updateAsset={onAssetUpdate} updating={updating} loadingAssetMetadata={loadingAssetMetadata} assetMetadata={assetMetadata}
                                                            selectedVersion={selectedVersion} assetVersions={assetVersions} 
                                                            assetResponse={assetResponse}
                                                        />
                                                        : selectedTab.name === 'files'
                                                            ? <Files asset={asset} editMode={editMode} updateAsset={onAssetUpdate} onUpload={fetchFiles} updating={updating} loadingAssetMetadata={loadingAssetMetadata} 
                                                                    assetMetadata={assetMetadata} selectedVersion={selectedVersion} assetVersions={assetVersions}
                                                                    assetResponse={assetResponse}
                                                                    latestVersionDate={latestVersionDate}
                                                                />
                                                            : 
                                                                selectedTab.name === 'options'
                                                                ? <Options 
                                                                    asset={asset} editMode={editMode} updateAsset={onAssetUpdate} updating={updating} loadingAssetMetadata={loadingAssetMetadata} 
                                                                    assetMetadata={assetMetadata} selectedVersion={selectedVersion} assetVersions={assetVersions}
                                                                    assetResponse={assetResponse}
                                                                />
                                                                : selectedTab.name === 'discussions'
                                                                && <Discussions 
                                                                    asset={asset} editMode={editMode} updateAsset={onAssetUpdate} updating={updating} loadingAssetMetadata={loadingAssetMetadata} 
                                                                    assetMetadata={assetMetadata} selectedVersion={selectedVersion} assetVersions={assetVersions}
                                                                    assetResponse={assetResponse}
                                                                />
                                            )
                                            // selectedTab.content({ asset, editMode, updateAsset: onAssetUpdate })
                                        }
                                    </div>
                                    <div id="asset-metadata">
                                        {
                                            asset &&
                                            <AssetMetadata 
                                                asset={asset} editMode={editMode} updateAsset={onAssetUpdate} updating={updating} loadingAssetMetadata={loadingAssetMetadata} 
                                                assetMetadata={assetMetadata} selectedVersion={selectedVersion} assetVersions={assetVersions} 
                                                assetResponse={assetResponse}
                                            />
                                        }
                                    </div>
                                </div>

                            </div>



                        </>
                        :
                        <>
                            <div id="asset-banner-loading" className="section loading">
                                <div id="asset-banner-content-loading">
                                    <div id="asset-banner-metadata-loading">
                                        <div id="asset-banner-type-loading" />
                                        <div id="asset-banner-title-loading" />
                                    </div>
                                </div>

                                <div id="asset-banner-image-loading">
                                </div>


                            </div>

                            <div className="section">
                                <div id="asset-tabs-loading">
                                    <div className="asset-loading-tab" />
                                    <div className="asset-loading-tab" />
                                    <div className="asset-loading-tab" />
                                    <div className="asset-loading-tab" />
                                </div>
                            </div>



                            <div id="asset-container-loading">
                                <div id="asset-container-content-loading" className="section">
                                    <div id="asset-content-loading" />
                                    <div id="asset-metadata-loading" />
                                </div>
                            </div>
                        </>
                }
            </div>
        </Page>
    )
}


export default AssetPage;